import React, { useEffect, useMemo, useRef, useState } from "react";
import { BACK_URI } from "../../config/keys";
import parse, { domToReact } from "html-react-parser";
import "./PogiConversationScreen.css";
import { resolveCustomerEmail } from "../../utils/customerEmail";

/**
 * Backend routes:
 *  GET  /api/pogi-conversation
 *  GET  /api/pogi-conversation/:sessionId
 *
 * NOTE: BACK_URI should be your server base (same one used by the Pogi chat page).
 */
const API_BASE = `${BACK_URI}/pogi-conversation`;

const MAX_LIMIT = 200;

function fmt(dt) {
  if (!dt) return "";
  const d = new Date(dt);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleString();
}

function fmtMoney(v) {
  if (v === null || v === undefined) return "";
  const n = Number(v);
  if (Number.isNaN(n)) return String(v);
  return n.toLocaleString();
}

function safeObj(v) {
  return v && typeof v === "object" && !Array.isArray(v) ? v : {};
}

function safeArr(v) {
  return Array.isArray(v) ? v : [];
}

function stripHtmlTags(html) {
  return String(html || "").replace(/<[^>]*>/g, "");
}

/**
 * Render message content with <span>...</span> underlined.
 * Uses html-react-parser replace() to swap span nodes for styled spans.
 */
function renderPogiHtml(content) {
  const raw = String(content || "");
  const normalized = raw.replace(/\n/g, "<br/>");

  let options;
  options = {
    replace: (domNode) => {
      if (domNode?.type === "tag" && domNode?.name === "span") {
        return (
          <span className="underline underline-offset-2 decoration-zinc-400 font-medium">
            {domToReact(domNode.children || [], options)}
          </span>
        );
      }
      return undefined;
    },
  };

  return parse(normalized, options);
}

function getCustomerFromMeta(meta) {
  const m = safeObj(meta);
  const c = safeObj(m.customer);
  const out = {};
  if (c.id) out.id = String(c.id).trim();
  const email = resolveCustomerEmail(c, m.customerEmail, m.email);
  if (email) out.email = email;
  if (c.firstName) out.firstName = String(c.firstName).trim();
  return Object.keys(out).length ? out : null;
}

function getRoutinePreviewFromMeta(meta) {
  const m = safeObj(meta);

  // From new backend previewMeta:
  // meta.routine is a compact summary
  const r = safeObj(m.routine);
  if (Object.keys(r).length) return r;

  // If meta is a raw assistant message meta and has routineSnapshot
  const snap = safeObj(m.routineSnapshot);
  if (Object.keys(snap).length) {
    // Make a compact summary for display
    const mode = snap.mode || null;
    const goal = snap.goal || null;
    const appliedCount = safeArr(snap.applied).length;
    const routineItemsCount = safeArr(snap.routineItems).length;
    const updatedAt = snap?.routine?.updatedAt || snap?.routine?.createdAt || null;
    return { mode, goal, appliedCount, routineItemsCount, updatedAt };
  }

  return null;
}

function getRoutineItemsFromMeta(meta) {
  const m = safeObj(meta);

  // Prefer routineSnapshot if available (more complete)
  const snap = safeObj(m.routineSnapshot);
  const snapItems = safeArr(snap.routineItems);
  if (snapItems.length) return snapItems;

  // fallback: recommendations.routineItems
  const rec = safeObj(m.recommendations);
  const recItems = safeArr(rec.routineItems);
  if (recItems.length) return recItems;

  return [];
}

function getRecommendationsFromMeta(meta) {
  const m = safeObj(meta);

  // New shape
  if (m.recommendations && typeof m.recommendations === "object") {
    const r = safeObj(m.recommendations);
    return {
      productResults: safeArr(r.productResults),
      similarProducts: safeArr(r.similarProducts),
      routineItems: safeArr(r.routineItems),
      lastProductSearch: r.lastProductSearch || null,
      lastRoutineSearch: r.lastRoutineSearch || null,
    };
  }

  // Compatibility fallback (if you ever nest)
  if (m.ui && typeof m.ui === "object") {
    const ui = safeObj(m.ui);
    const r = safeObj(ui.recommendations);
    return {
      productResults: safeArr(r.productResults),
      similarProducts: safeArr(r.similarProducts),
      routineItems: safeArr(r.routineItems),
      lastProductSearch: r.lastProductSearch || null,
      lastRoutineSearch: r.lastRoutineSearch || null,
    };
  }

  return null;
}

async function fetchJson(url, opts = {}) {
  let token = "";
  try {
    token = JSON.parse(localStorage.getItem("userInfo") || "null")?.token || "";
  } catch {
    token = "";
  }
  const res = await fetch(url, {
    headers: {
      "Content-Type": "application/json",
      ...(token ? { Authorization: token } : {}),
      ...(opts.headers || {}),
    },
    ...opts,
  });

  const text = await res.text();
  let data = null;
  try {
    data = text ? JSON.parse(text) : null;
  } catch {
    // ignore parse failure
  }

  if (!res.ok) {
    const msg = data?.msg || data?.error || data?.message || `Request failed (${res.status})`;
    const err = new Error(msg);
    err.status = res.status;
    err.data = data;
    throw err;
  }

  return data;
}

function classNames(...xs) {
  return xs.filter(Boolean).join(" ");
}

function RoleBadge({ role }) {
  const styles =
    role === "user"
      ? "bg-blue-100 text-blue-800"
      : role === "assistant"
        ? "bg-emerald-100 text-emerald-800"
        : "bg-zinc-200 text-zinc-800";

  return (
    <span className={classNames("px-2 py-0.5 rounded text-xs font-medium", styles)}>
      {role || "system"}
    </span>
  );
}

function MiniPill({ text }) {
  return (
    <span className="inline-flex items-center rounded-full border border-zinc-200 bg-zinc-50 px-2 py-0.5 text-[11px] text-zinc-700">
      {text}
    </span>
  );
}

function StepLabel({ stepKey }) {
  const map = {
    cleanser: "Cleanser",
    toner: "Toner",
    serum: "Serum",
    ampoule: "Ampoule",
    essence: "Essence",
    emulsion: "Emulsion",
    lotion: "Lotion",
    treatment: "Treatment",
    moisturizer: "Moisturizer",
    sunscreen: "Sunscreen",
    exfoliator: "Exfoliator",
    eye_cream: "Eye Cream",
    eye_patch: "Eye Patch",
    spot_treatment: "Spot Treatment",
    sleeping_mask: "Sleeping Mask",
    sheet_mask: "Sheet Mask",
    pore_mask: "Pore Mask",
    gel_mask: "Gel Mask",
  };
  const s = String(stepKey || "")
    .trim()
    .toLowerCase();
  if (map[s]) return map[s];
  if (!s) return "Step";
  return s
    .replace(/_/g, " ")
    .split(" ")
    .map((w) => (w ? `${w[0].toUpperCase()}${w.slice(1)}` : ""))
    .join(" ");
}

function TimeBadge({ timeOfDay }) {
  const t = String(timeOfDay || "").toLowerCase();
  const label = t === "am_pm" ? "AM/PM" : t === "am" ? "AM" : t === "pm" ? "PM" : "";
  if (!label) return null;

  return (
    <span className="inline-flex items-center rounded-full border border-zinc-200 bg-white px-2 py-0.5 text-[10px] font-semibold text-zinc-700">
      {label}
    </span>
  );
}

function ProductCard({ p }) {
  if (!p) return null;

  const name = p.name || "Untitled";
  const brand = p.brand || "";
  const imageUrl = p.imageUrl || null;
  const rating = p.rating ?? null;
  const numReviews = p.numReviews ?? null;

  const price = p.price ?? null;
  const mrp = p.mrp ?? null;

  return (
    <div className="rounded-xl border border-zinc-200 bg-white overflow-hidden">
      <div className="flex gap-3 p-3">
        <div className="h-14 w-14 rounded-lg bg-zinc-100 border border-zinc-200 overflow-hidden flex items-center justify-center shrink-0">
          {imageUrl ? (
            <img src={imageUrl} alt={name} className="h-full w-full object-cover" />
          ) : (
            <span className="text-[10px] text-zinc-400">No image</span>
          )}
        </div>

        <div className="min-w-0 flex-1">
          <div className="text-sm font-semibold text-zinc-900 truncate">{name}</div>
          {brand ? <div className="text-xs text-zinc-500 truncate">{brand}</div> : null}

          <div className="mt-1 flex flex-wrap gap-2 items-center">
            {price !== null ? (
              <span className="text-xs font-medium text-zinc-900">₱ {fmtMoney(price)}</span>
            ) : null}
            {mrp !== null ? (
              <span className="text-xs text-zinc-400 line-through">₱ {fmtMoney(mrp)}</span>
            ) : null}

            {rating !== null ? (
              <span className="text-xs text-zinc-600">
                ★ {Number(rating).toFixed(1)}
                {typeof numReviews === "number" ? ` (${numReviews})` : ""}
              </span>
            ) : null}
          </div>

          {Array.isArray(p.filterTags) && p.filterTags.length ? (
            <div className="mt-2 flex flex-wrap gap-1">
              {p.filterTags.slice(0, 3).map((t) => (
                <span
                  key={t}
                  className="inline-flex items-center rounded-md bg-zinc-100 px-1.5 py-0.5 text-[10px] text-zinc-700"
                >
                  {t}
                </span>
              ))}
              {p.filterTags.length > 3 ? (
                <span className="text-[10px] text-zinc-500">+{p.filterTags.length - 3} more</span>
              ) : null}
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
}

function ProductGridSection({ title, items }) {
  const list = safeArr(items);
  if (!list.length) return null;

  return (
    <div className="mt-3">
      <div className="text-xs font-semibold text-zinc-700 mb-2">{title}</div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
        {list.map((p, i) => (
          <ProductCard key={p.id || p.slug || `${title}-${i}`} p={p} />
        ))}
      </div>
    </div>
  );
}

function RoutineItemCard({ item }) {
  const it = safeObj(item);
  const product = safeObj(it.product);

  const name = product.name || "Product";
  const brand = product.brand || "";
  const imageUrl = product.imageUrl || null;

  return (
    <div className="rounded-xl border border-zinc-200 bg-white overflow-hidden">
      <div className="flex gap-3 p-3">
        <div className="h-14 w-14 rounded-lg bg-zinc-100 border border-zinc-200 overflow-hidden flex items-center justify-center shrink-0">
          {imageUrl ? (
            <img src={imageUrl} alt={name} className="h-full w-full object-cover" />
          ) : (
            <span className="text-[10px] text-zinc-400">No image</span>
          )}
        </div>

        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-xs font-extrabold text-zinc-900">
              <StepLabel stepKey={it.stepKey} />
            </span>
            <TimeBadge timeOfDay={it.timeOfDay} />
            {it.frequency && it.frequency !== "daily" ? (
              <span className="text-[11px] text-zinc-500">• {String(it.frequency)}</span>
            ) : null}
          </div>

          <div className="mt-1 text-sm font-semibold text-zinc-900 truncate">{name}</div>
          {brand ? <div className="text-xs text-zinc-500 truncate">{brand}</div> : null}

          {(it.usageNotes || it.whyPicked) && (
            <div className="mt-2 text-xs text-zinc-700 space-y-1">
              {it.usageNotes ? (
                <div className="line-clamp-2">
                  <span className="font-semibold">How: </span>
                  {stripHtmlTags(String(it.usageNotes))}
                </div>
              ) : null}
              {it.whyPicked ? (
                <div className="line-clamp-2">
                  <span className="font-semibold">Why: </span>
                  {stripHtmlTags(String(it.whyPicked))}
                </div>
              ) : null}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function RoutineGridSection({ title, items }) {
  const list = safeArr(items);
  if (!list.length) return null;

  return (
    <div className="mt-3">
      <div className="text-xs font-semibold text-zinc-700 mb-2">{title}</div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
        {list.map((it, i) => (
          <RoutineItemCard key={it.key || `${it.stepKey}:${it.timeOfDay}:${i}`} item={it} />
        ))}
      </div>
    </div>
  );
}

function SearchDebug({ lastProductSearch }) {
  const s = safeObj(lastProductSearch);
  if (!Object.keys(s).length) return null;

  const filters = safeObj(s.filters);

  return (
    <details className="mt-3">
      <summary className="text-xs text-zinc-500 cursor-pointer select-none">
        Search debug (query/filters)
      </summary>

      <div className="mt-2 rounded-lg border border-zinc-200 bg-white/60 p-3 text-xs text-zinc-700 space-y-1">
        {s.query ? (
          <div>
            <span className="font-semibold">query:</span> {String(s.query)}
          </div>
        ) : null}
        {s.step ? (
          <div>
            <span className="font-semibold">step:</span> {String(s.step)}
          </div>
        ) : null}
        {s.sortMode ? (
          <div>
            <span className="font-semibold">sortMode:</span> {String(s.sortMode)}
          </div>
        ) : null}
        {typeof s.limit === "number" ? (
          <div>
            <span className="font-semibold">limit:</span> {s.limit}
          </div>
        ) : null}
        {s.at ? (
          <div>
            <span className="font-semibold">at:</span> {String(s.at)}
          </div>
        ) : null}

        {Object.keys(filters).length ? (
          <div className="pt-2">
            <div className="font-semibold mb-1">filters:</div>
            <pre className="text-[11px] overflow-auto">{JSON.stringify(filters, null, 2)}</pre>
          </div>
        ) : null}
      </div>
    </details>
  );
}

function RoutineDebug({ lastRoutineSearch }) {
  const s = safeObj(lastRoutineSearch);
  if (!Object.keys(s).length) return null;

  return (
    <details className="mt-3">
      <summary className="text-xs text-zinc-500 cursor-pointer select-none">Routine debug</summary>

      <div className="mt-2 rounded-lg border border-zinc-200 bg-white/60 p-3 text-xs text-zinc-700 space-y-1">
        {s.mode ? (
          <div>
            <span className="font-semibold">mode:</span> {String(s.mode)}
          </div>
        ) : null}
        {s.goal ? (
          <div>
            <span className="font-semibold">goal:</span> {String(s.goal)}
          </div>
        ) : null}
        {typeof s.slotCount === "number" ? (
          <div>
            <span className="font-semibold">slotCount:</span> {s.slotCount}
          </div>
        ) : null}
        {s.at ? (
          <div>
            <span className="font-semibold">at:</span> {String(s.at)}
          </div>
        ) : null}
      </div>
    </details>
  );
}

export default function PogiConversationScreen() {
  // ---- Sessions list state ----
  const [conversations, setConversations] = useState([]);
  const [listNextCursor, setListNextCursor] = useState(null);
  const [listLoading, setListLoading] = useState(false);
  const [listError, setListError] = useState("");

  const [listQuery, setListQuery] = useState(""); // client-side filter only

  const filteredConversations = useMemo(() => {
    const q = listQuery.trim().toLowerCase();
    if (!q) return conversations;

    return conversations.filter((c) => {
      const sid = String(c.sessionId || "").toLowerCase();

      const previewMeta = c?.preview?.meta ? safeObj(c.preview.meta) : {};
      const customer = safeObj(previewMeta.customer);
      const customerEmail = String(customer.email || "").toLowerCase();
      const customerFirstName = String(customer.firstName || "").toLowerCase();

      return (
        sid.includes(q) ||
        customerEmail.includes(q) ||
        customerFirstName.includes(q) ||
        String(c.customerId || "")
          .toLowerCase()
          .includes(q)
      );
    });
  }, [conversations, listQuery]);

  // ---- Selected conversation state ----
  const [selectedSessionId, setSelectedSessionId] = useState(null);
  const [selectedMeta, setSelectedMeta] = useState(null); // from list row

  const [messages, setMessages] = useState([]); // oldest-first for UI
  const messagesRef = useRef([]);
  useEffect(() => {
    messagesRef.current = messages;
  }, [messages]);

  const [msgLoading, setMsgLoading] = useState(false);
  const [msgError, setMsgError] = useState("");
  const [msgHasMore, setMsgHasMore] = useState(true);

  const messagesEndRef = useRef(null);

  function scrollToBottom(smooth = true) {
    const el = messagesEndRef.current;
    if (!el) return;
    el.scrollIntoView({ behavior: smooth ? "smooth" : "auto" });
  }

  // ---- Load sessions list ----
  async function loadConversationList({ reset = false } = {}) {
    setListError("");
    setListLoading(true);

    try {
      const cursor = reset ? null : listNextCursor;
      const url = cursor
        ? `${API_BASE}?before=${encodeURIComponent(cursor)}&limit=50`
        : `${API_BASE}?limit=50`;

      const data = await fetchJson(url, { method: "GET" });

      const newRows = safeArr(data?.conversations);
      const next = data?.nextCursor || null;

      setConversations((prev) => (reset ? newRows : [...prev, ...newRows]));
      setListNextCursor(next);
    } catch (e) {
      setListError(e?.message || "Failed to load conversations");
    } finally {
      setListLoading(false);
    }
  }

  // ---- Load a session's messages ----
  async function loadSessionMessages(sessionId, { reset = false } = {}) {
    if (!sessionId) return;

    setMsgError("");
    setMsgLoading(true);

    try {
      const limit = 50;

      let beforeParam = "";
      if (!reset && messagesRef.current.length > 0) {
        // oldest message is first in our UI list
        const oldest = messagesRef.current[0]?.createdAt;
        if (oldest) beforeParam = `&before=${encodeURIComponent(oldest)}`;
      }

      const url = `${API_BASE}/${encodeURIComponent(sessionId)}?limit=${Math.min(
        limit,
        MAX_LIMIT,
      )}${beforeParam}`;

      const data = await fetchJson(url, { method: "GET" });

      const incomingDesc = safeArr(data?.messages); // backend returns newest-first
      const incomingAsc = [...incomingDesc].reverse(); // UI wants oldest-first

      setMessages((prev) => {
        if (reset) return incomingAsc;
        // prepend older messages
        return [...incomingAsc, ...prev];
      });

      setMsgHasMore(incomingDesc.length >= limit);

      if (reset) {
        setTimeout(() => scrollToBottom(false), 50);
      }
    } catch (e) {
      setMsgError(e?.message || "Failed to load messages");
    } finally {
      setMsgLoading(false);
    }
  }

  async function handleSelectConversation(row) {
    const sessionId = row?.sessionId;
    if (!sessionId) return;

    setSelectedSessionId(sessionId);
    setSelectedMeta(row);
    setMessages([]);
    setMsgHasMore(true);

    await loadSessionMessages(sessionId, { reset: true });
  }

  // Initial list load
  useEffect(() => {
    loadConversationList({ reset: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const selectedTitle = selectedSessionId
    ? `Session: ${selectedSessionId}`
    : "Select a conversation";

  const selectedPreviewMeta = selectedMeta?.preview?.meta
    ? safeObj(selectedMeta.preview.meta)
    : null;

  const selectedPreviewRecs = selectedPreviewMeta?.recommendations
    ? safeObj(selectedPreviewMeta.recommendations)
    : null;

  const selectedCustomer = selectedPreviewMeta ? getCustomerFromMeta(selectedPreviewMeta) : null;
  const selectedRoutinePreview = selectedPreviewMeta
    ? getRoutinePreviewFromMeta(selectedPreviewMeta)
    : null;

  return (
    <div className="pogi-conversations-admin">
      <div className="pogi-conversations-admin__layout">
        {/* LEFT: Conversation list */}
        <aside className="pogi-conversations-admin__sidebar">
          <div className="p-4 border-b border-zinc-200">
            <div className="flex items-center justify-between gap-2">
              <h1 className="text-lg font-semibold text-zinc-900">Pogi Conversations</h1>
              <button
                onClick={() => loadConversationList({ reset: true })}
                disabled={listLoading}
                className="px-3 py-1.5 rounded-md text-sm font-medium border border-zinc-300 hover:bg-zinc-50 disabled:opacity-50"
              >
                {listLoading ? "Refreshing..." : "Refresh"}
              </button>
            </div>

            <div className="mt-3">
              <input
                value={listQuery}
                onChange={(e) => setListQuery(e.target.value)}
                placeholder="Filter by sessionId / email / name..."
                className="w-full px-3 py-2 rounded-md border border-zinc-300 focus:outline-none focus:ring-2 focus:ring-zinc-400"
              />
            </div>

            {listError ? <div className="mt-3 text-sm text-red-600">{listError}</div> : null}
          </div>

          <div className="flex-1 overflow-auto">
            {filteredConversations.length === 0 && !listLoading ? (
              <div className="p-4 text-sm text-zinc-500">No conversations found.</div>
            ) : null}

            <ul className="divide-y divide-zinc-100">
              {filteredConversations.map((c) => {
                const active = c.sessionId === selectedSessionId;

                const previewText = stripHtmlTags(
                  c?.preview?.content ? String(c.preview.content) : "",
                );

                const previewMeta = c?.preview?.meta ? safeObj(c.preview.meta) : null;
                const rec = previewMeta?.recommendations
                  ? safeObj(previewMeta.recommendations)
                  : null;

                const customer = previewMeta ? getCustomerFromMeta(previewMeta) : null;
                const routinePrev = previewMeta ? getRoutinePreviewFromMeta(previewMeta) : null;

                const resultsCount = rec?.resultsCount ?? null;
                const similarCount = rec?.similarCount ?? null;
                const routineItemsCount =
                  rec?.routineItemsCount ?? routinePrev?.routineItemsCount ?? null;

                const miniResults = safeArr(rec?.productResults);
                const miniSimilar = safeArr(rec?.similarProducts);
                const miniRoutine = safeArr(rec?.routineItems);

                return (
                  <li key={c.sessionId}>
                    <button
                      onClick={() => handleSelectConversation(c)}
                      className={classNames(
                        "w-full text-left px-4 py-3 hover:bg-zinc-50",
                        active ? "bg-zinc-100" : "",
                      )}
                    >
                      <div className="min-w-0">
                        <div className="text-sm font-semibold text-zinc-900 truncate">
                          {c.sessionId}
                        </div>

                        <div className="mt-1 flex items-center gap-2">
                          <span className="text-xs text-zinc-500">{c.messageCount || 0} msgs</span>
                          <span className="text-xs text-zinc-400">•</span>
                          <span className="text-xs text-zinc-500">{fmt(c.lastMessageAt)}</span>
                        </div>

                        {/* NEW: customer */}
                        {customer?.email || customer?.firstName ? (
                          <div className="mt-2 text-xs text-zinc-600 truncate">
                            👤 {customer.firstName ? `${customer.firstName}` : "Customer"}
                            {customer.email ? ` • ${customer.email}` : ""}
                          </div>
                        ) : null}

                        {/* NEW: counts */}
                        {rec && (resultsCount || similarCount || routineItemsCount) ? (
                          <div className="mt-2 flex items-center gap-2 flex-wrap">
                            {typeof resultsCount === "number" ? (
                              <MiniPill text={`🔎 results: ${resultsCount}`} />
                            ) : null}
                            {typeof similarCount === "number" ? (
                              <MiniPill text={`↔ similar: ${similarCount}`} />
                            ) : null}
                            {typeof routineItemsCount === "number" ? (
                              <MiniPill text={`🧴 routine: ${routineItemsCount}`} />
                            ) : null}
                            {routinePrev?.mode ? (
                              <MiniPill text={`mode: ${routinePrev.mode}`} />
                            ) : null}
                          </div>
                        ) : null}

                        {/* NEW: mini chips */}
                        {miniResults.length > 0 ||
                        miniSimilar.length > 0 ||
                        miniRoutine.length > 0 ? (
                          <div className="mt-2 flex flex-wrap gap-1">
                            {miniRoutine.slice(0, 2).map((it, i) => (
                              <MiniPill
                                key={it.key || `${it.stepKey}:${it.timeOfDay}:${i}`}
                                text={`🧴 ${it?.product?.name || "Routine item"}`}
                              />
                            ))}
                            {miniResults.slice(0, 2).map((p, i) => (
                              <MiniPill
                                key={p.id || p.slug || `r-${i}`}
                                text={`🔎 ${p.name || "Product"}`}
                              />
                            ))}
                            {miniSimilar.slice(0, 2).map((p, i) => (
                              <MiniPill
                                key={p.id || p.slug || `s-${i}`}
                                text={`↔ ${p.name || "Product"}`}
                              />
                            ))}
                          </div>
                        ) : null}

                        {previewText ? (
                          <div className="mt-2 text-xs text-zinc-600 line-clamp-2">
                            <span className="mr-2 inline-block align-middle">
                              <RoleBadge role={c.preview?.role || "system"} />
                            </span>
                            <span className="align-middle">{previewText}</span>
                          </div>
                        ) : (
                          <div className="mt-2 text-xs text-zinc-400">No preview</div>
                        )}
                      </div>
                    </button>
                  </li>
                );
              })}
            </ul>

            <div className="p-4">
              <button
                onClick={() => loadConversationList({ reset: false })}
                disabled={listLoading || !listNextCursor}
                className="w-full px-3 py-2 rounded-md text-sm font-medium border border-zinc-300 hover:bg-zinc-50 disabled:opacity-50"
              >
                {listLoading ? "Loading..." : listNextCursor ? "Load more" : "No more"}
              </button>
            </div>
          </div>
        </aside>

        {/* RIGHT: Conversation detail */}
        <main className="pogi-conversations-admin__detail">
          <div className="px-6 py-4 border-b border-zinc-200 bg-white flex items-center justify-between gap-3">
            <div className="min-w-0">
              <div className="text-base font-semibold text-zinc-900 truncate">{selectedTitle}</div>

              {selectedMeta ? (
                <div className="mt-1 text-xs text-zinc-500">
                  Last: {fmt(selectedMeta.lastMessageAt)} • Count: {selectedMeta.messageCount || 0}
                </div>
              ) : (
                <div className="mt-1 text-xs text-zinc-500">Pick a session to view messages.</div>
              )}

              {/* NEW: customer header */}
              {selectedCustomer?.email || selectedCustomer?.firstName ? (
                <div className="mt-2 text-xs text-zinc-700">
                  👤{" "}
                  <span className="font-semibold">{selectedCustomer.firstName || "Customer"}</span>
                  {selectedCustomer.email ? (
                    <span className="text-zinc-500"> • {selectedCustomer.email}</span>
                  ) : null}
                </div>
              ) : null}

              {/* NEW: header summary of recs + routine */}
              {selectedPreviewRecs || selectedRoutinePreview ? (
                <div className="mt-2 flex items-center gap-2 flex-wrap">
                  {typeof selectedPreviewRecs?.resultsCount === "number" ? (
                    <MiniPill text={`🔎 results: ${selectedPreviewRecs.resultsCount}`} />
                  ) : null}
                  {typeof selectedPreviewRecs?.similarCount === "number" ? (
                    <MiniPill text={`↔ similar: ${selectedPreviewRecs.similarCount}`} />
                  ) : null}
                  {typeof selectedPreviewRecs?.routineItemsCount === "number" ? (
                    <MiniPill text={`🧴 routine: ${selectedPreviewRecs.routineItemsCount}`} />
                  ) : typeof selectedRoutinePreview?.routineItemsCount === "number" ? (
                    <MiniPill text={`🧴 routine: ${selectedRoutinePreview.routineItemsCount}`} />
                  ) : null}
                  {selectedRoutinePreview?.mode ? (
                    <MiniPill text={`mode: ${selectedRoutinePreview.mode}`} />
                  ) : null}
                  {selectedRoutinePreview?.goal ? (
                    <MiniPill text={`goal: ${String(selectedRoutinePreview.goal).slice(0, 40)}`} />
                  ) : null}
                </div>
              ) : null}
            </div>

            <div className="flex items-center gap-2">
              <button
                onClick={() =>
                  selectedSessionId && loadSessionMessages(selectedSessionId, { reset: true })
                }
                disabled={!selectedSessionId || msgLoading}
                className="px-3 py-1.5 rounded-md text-sm font-medium border border-zinc-300 hover:bg-zinc-50 disabled:opacity-50"
              >
                {msgLoading ? "Reloading..." : "Reload"}
              </button>

              <button
                onClick={() => selectedSessionId && scrollToBottom(true)}
                disabled={!selectedSessionId}
                className="px-3 py-1.5 rounded-md text-sm font-medium border border-zinc-300 hover:bg-zinc-50 disabled:opacity-50"
              >
                Bottom
              </button>
            </div>
          </div>

          {/* Messages */}
          <div className="flex-1 overflow-auto px-6 py-4">
            {!selectedSessionId ? (
              <div className="h-full flex items-center justify-center text-zinc-500">
                Select a conversation from the left.
              </div>
            ) : (
              <>
                <div className="flex items-center justify-center mb-4">
                  <button
                    onClick={() => loadSessionMessages(selectedSessionId, { reset: false })}
                    disabled={msgLoading || !msgHasMore}
                    className="px-3 py-2 rounded-md text-sm font-medium border border-zinc-300 hover:bg-zinc-50 disabled:opacity-50"
                  >
                    {msgLoading
                      ? "Loading..."
                      : msgHasMore
                        ? "Load older messages"
                        : "No older messages"}
                  </button>
                </div>

                {msgError ? <div className="mb-4 text-sm text-red-600">{msgError}</div> : null}

                <div className="space-y-3">
                  {messages.map((m, idx) => {
                    const isUser = m.role === "user";
                    const bubble = isUser
                      ? "bg-blue-50 border-blue-100"
                      : m.role === "assistant"
                        ? "bg-emerald-50 border-emerald-100"
                        : "bg-zinc-100 border-zinc-200";

                    const align = isUser ? "items-start" : "items-end";

                    const rec = m.role === "assistant" ? getRecommendationsFromMeta(m.meta) : null;
                    const hasRecs =
                      !!rec &&
                      (safeArr(rec.productResults).length > 0 ||
                        safeArr(rec.similarProducts).length > 0);
                    const responseIntent =
                      m.role === "assistant" ? String(m?.meta?.responseIntent || "").trim() : "";
                    const uiMode =
                      m.role === "assistant" ? String(m?.meta?.uiMode || "").trim() : "";
                    const language =
                      m.role === "assistant"
                        ? String(m?.meta?.language || m?.meta?.clientContext?.language || "").trim()
                        : "";

                    const productContext = m?.meta?.productContext
                      ? safeObj(m.meta.productContext)
                      : null;

                    // NEW: routine rendering (from routineSnapshot or recommendations)
                    const routineItems =
                      m.role === "assistant" ? getRoutineItemsFromMeta(m.meta) : [];
                    const hasRoutine = routineItems.length > 0;

                    const lastRoutineSearch =
                      rec?.lastRoutineSearch ||
                      safeObj(m?.meta?.routineSnapshot)?.lastRoutineSearch ||
                      null;

                    const routinePrev =
                      m.role === "assistant" ? getRoutinePreviewFromMeta(m.meta) : null;

                    return (
                      <div
                        key={m._id || `${m.createdAt || idx}-${idx}`}
                        className={classNames("flex flex-col", align)}
                      >
                        <div className="flex items-center gap-2 mb-1">
                          <RoleBadge role={m.role || "system"} />
                          <span className="text-xs text-zinc-500">{fmt(m.createdAt)}</span>

                          <button
                            type="button"
                            className="ml-1 text-xs text-zinc-400 hover:text-zinc-600"
                            onClick={() =>
                              navigator.clipboard?.writeText?.(
                                stripHtmlTags(String(m.content || "")),
                              )
                            }
                            title="Copy message"
                          >
                            Copy
                          </button>

                          {m.turnId ? (
                            <span className="ml-2 text-[11px] text-zinc-400">turn: {m.turnId}</span>
                          ) : null}
                        </div>

                        <div
                          className={classNames(
                            "max-w-[78%] rounded-xl border px-4 py-2 text-sm text-zinc-900",
                            bubble,
                          )}
                        >
                          <div className="break-words">{renderPogiHtml(m.content)}</div>

                          {/* NEW: assistant extras (context / routine / recommendations) */}
                          {m.role === "assistant" &&
                          (productContext ||
                          hasRoutine ||
                          hasRecs ||
                          routinePrev ||
                          responseIntent ||
                          uiMode ||
                          language) ? (
                            <div className="mt-3 border-t border-zinc-200/70 pt-3">
                              {responseIntent || uiMode || language ? (
                                <div className="mb-2 flex flex-wrap gap-2 items-center">
                                  {responseIntent ? (
                                    <MiniPill text={`intent: ${responseIntent}`} />
                                  ) : null}
                                  {uiMode ? <MiniPill text={`ui: ${uiMode}`} /> : null}
                                  {language ? <MiniPill text={`lang: ${language}`} /> : null}
                                </div>
                              ) : null}

                              {productContext?.name ? (
                                <div className="mb-2 text-xs text-zinc-600">
                                  <span className="font-semibold">Context:</span>{" "}
                                  {productContext.brand ? `${productContext.brand} — ` : ""}
                                  {productContext.name}
                                </div>
                              ) : null}

                              {routinePrev ? (
                                <div className="mb-2 flex flex-wrap gap-2 items-center">
                                  {routinePrev.mode ? (
                                    <MiniPill text={`routine mode: ${routinePrev.mode}`} />
                                  ) : null}
                                  {typeof routinePrev.appliedCount === "number" ? (
                                    <MiniPill text={`applied: ${routinePrev.appliedCount}`} />
                                  ) : null}
                                  {typeof routinePrev.routineItemsCount === "number" ? (
                                    <MiniPill text={`items: ${routinePrev.routineItemsCount}`} />
                                  ) : null}
                                  {routinePrev.updatedAt ? (
                                    <MiniPill text={`updated: ${fmt(routinePrev.updatedAt)}`} />
                                  ) : null}
                                </div>
                              ) : null}

                              {hasRoutine ? (
                                <>
                                  <RoutineGridSection
                                    title={`Routine Items (${routineItems.length})`}
                                    items={routineItems}
                                  />
                                  <RoutineDebug lastRoutineSearch={lastRoutineSearch} />
                                </>
                              ) : null}

                              {hasRecs ? (
                                <>
                                  <ProductGridSection
                                    title={`Recommended Products (${safeArr(rec.productResults).length})`}
                                    items={rec.productResults}
                                  />
                                  <ProductGridSection
                                    title={`Similar Products (${safeArr(rec.similarProducts).length})`}
                                    items={rec.similarProducts}
                                  />
                                  <SearchDebug lastProductSearch={rec.lastProductSearch} />
                                </>
                              ) : null}
                            </div>
                          ) : null}

                          {/* Keep raw meta for debugging */}
                          {m.meta && Object.keys(m.meta || {}).length ? (
                            <details className="mt-3">
                              <summary className="text-xs text-zinc-500 cursor-pointer select-none">
                                meta
                              </summary>
                              <pre className="mt-2 text-xs bg-white/60 p-2 rounded border border-zinc-200 overflow-auto">
                                {JSON.stringify(m.meta, null, 2)}
                              </pre>
                            </details>
                          ) : null}
                        </div>
                      </div>
                    );
                  })}
                  <div ref={messagesEndRef} />
                </div>
              </>
            )}
          </div>

          {/* Footer */}
          <div className="border-t border-zinc-200 bg-white px-6 py-3">
            <div className="text-xs text-zinc-500">
              Use the left panel to choose a session • “Load older messages” for pagination.
            </div>
          </div>
        </main>
      </div>
    </div>
  );
}
